/* **`/konto`** — the two-column account screen, from `profile-web-mockups-v2/f-zakladki.html`.
 *
 * The mockup is written in literal CSS; every value below is the token that carries it. Where a
 * number is pure layout — a column width, a row's floor — it is a token in `tokens/spacing.css`
 * under `--*-account-*` rather than a literal here, which is the house rule rather than stylelint's
 * (heights and widths are deliberately outside the value allowlist).
 *
 * ── Why the page is NOT `canonScreen`'s measure ──────────────────────────────────────────────
 * `shared/ui/canonScreen.module.css` gives a v2 screen a bounded `<main>` and a ~1600px measure
 * centred inside it. This screen takes the `.page` — the bound is what stops the document scrolling
 * and the chrome leaving — and NOT the measure, because its two columns run to the frame's edges by
 * design: the left sheet is a `--bg-card` surface with a hairline on its right, and the right
 * column stands on the canvas. The measure lives one level in, on the sheet's content, which is
 * what `--gut` computes.
 *
 * ── The right column is ALWAYS present ───────────────────────────────────────────────────────
 * Every tab puts its one supporting object there, so nothing shifts between tabs. Under 1080px it
 * is hidden outright rather than narrowed — a trainer-code card at 240px is a card nobody can read,
 * and the left column is self-sufficient without it. That number stays a literal in the media query
 * below, because a media feature cannot read a custom property; nothing else in this app reads it,
 * so there is no second opinion for it to disagree with.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* The two columns. `minmax(0, 1fr)` on the left so the sheet can actually shrink: a grid item's
 * default `min-width: auto` refuses to go below its content, and the form inside is wider than a
 * 1280px frame minus the side column. */
.columns {
  display: grid;
  flex: 1;
  grid-template-columns: minmax(0, 1fr) var(--w-account-side);
  min-height: 0;
}

/* The sheet. `--gut` is the mockup's own: 48px of inset, or whatever it takes to centre a 760px
 * measure once the column is wider than that. Stated once here and read by every row below, which
 * is what keeps the header, the tab strip and the content on one vertical line. */
.sheet {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--bg);
  border-right: var(--border-hairline) solid var(--border);

  --gut: max(var(--space-12), calc((100% - var(--w-account-measure)) / 2));
}

/* The header band: the title alone („Wyloguj się" moved to Profil on 4 November 2026). It is the `screenHeader` CLASSES
 * rather than the `ScreenHeader` component — that component always draws a `<dl>` of statistics and
 * this screen has none, so an empty definition list plus its row gap would be geometry nobody asked
 * for. `/raporty` takes the same decision for the same reason. */
.header {
  flex: none;
  padding: var(--space-30px) var(--gut) var(--space-0);
}

/* The title row keeps the height „Wyloguj się" gave it while it stood here, so the tab strip and
 * everything under it stayed where the canon draws them when the button moved to Profil. */
.titleRow {
  min-height: var(--h-account-button);
}

/* The strip's BAR — its box, and nothing `TabStrip` itself declares. 54px tall with the tabs
 * sitting on the bottom hairline, which is where the rule opens. */
.tabBar {
  flex: none;
  gap: var(--space-6);
  align-items: flex-end;
  height: var(--h-account-tabbar);
  padding: var(--space-0) var(--gut);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The one box that scrolls on this screen. `scrollbar-gutter: stable` so a tab whose content
 * overflows does not shift the measure sideways against a tab whose content does not — and on
 * BOTH edges, because a gutter reserved on the right alone moves the centred measure half a
 * scrollbar left of the header and the tab strip above it (7px at 1512 with classic scrollbars).
 * Reserved on both sides, the content's left edge stands on the header's line. */
.panel {
  flex: 1;
  min-height: 0;
  overflow: hidden auto;
  scrollbar-gutter: stable both-edges;
}

/* The panel's own inset, and the measure. The Profil tab wears `.tight` on top of this, which is
 * the mockup's `.inner.tight` — the cut that makes that tab fit a 1280x720 laptop with no scroll. */
.inner {
  padding: var(--space-7) var(--gut);
}

.tight {
  padding-block: var(--space-6);
}

.lede {
  margin-bottom: var(--space-4);
}

.tight .lede {
  margin-bottom: var(--space-3);
}

.ledeText {
  max-width: var(--w-account-lede);
  font-size: var(--fs-body);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* A block: the mockup's `.blk`, separated by a rule rather than by a card. */
.block {
  padding: var(--space-5) var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
}

.tight .block {
  padding: var(--space-4) var(--space-0);
}

.blockTitle {
  display: flex;
  gap: var(--space-2-5px);
  align-items: center;
  margin-bottom: var(--space-14px);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* A block of ROWS — the Aplikacja tab's three. The mockup's `.blk.rows`, which drops the block's
 * own padding because each row already carries its own rule. */
.rows {
  padding: var(--space-1) var(--space-0);
}

.row {
  display: flex;
  gap: var(--space-14px);
  align-items: center;
  min-height: var(--h-account-row);
  padding: var(--space-3) var(--space-0);
  border-top: var(--border-hairline) solid var(--border);
}

.row:first-child {
  border-top: 0;
}

.rowText {
  flex: 1;
  min-width: 0;
}

.rowTitle {
  display: block;
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-semibold);
  letter-spacing: var(--ls-account-row);
  color: var(--text);
}

.rowNote {
  display: block;
  margin-top: var(--space-1px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.rowValue {
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.rowIcon {
  display: block;
  flex: none;
  width: var(--sz-account-glyph);
  height: var(--sz-account-glyph);
  color: var(--text-2);
}

/* ── The screen's three action paints (owner-approved polish, 4 November 2026) ─────────────
 * ONE primary paint: `shared/ui/emberButton`'s `.ember.pill`, refused as `.quiet` — a transparent
 * outline rather than a grey slab. Beside it a secondary outline pill in body ink and a destructive
 * one, `shared/ui/ghostButton`'s `.ghost.ink` and `.ghost.danger`. All three wear
 * `shared/ui/pillButton`'s shape; `.pill` here is the screen's SIZE, the two values the Pulpit's pill
 * sizes differently. `ui/accountButtons.ts` spells the three class lists once. */
.pill {
  height: var(--h-account-button);
  font-size: var(--fs-note);
}

.pillGlyph {
  display: block;
  flex: none;
  width: var(--sz-account-glyph);
  height: var(--sz-account-glyph);
}

/* ── Grouped settings rows, macOS System Settings style ──────────────────────────────────────
 * A small label over a card of rows. Every tab composes its settings from these four classes,
 * so a row on Profil and a row on Subskrypcja are the same object. */
.groupLabel {
  margin-bottom: var(--space-2);
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

.group {
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-account-group);
}

/* The room between one group and the next thing under it. */
.group + .groupLabel,
.group + .group,
.groupHint + .groupLabel {
  margin-top: var(--space-5);
}

.groupRow {
  display: flex;
  gap: var(--space-4);
  align-items: center;
  min-height: var(--h-account-group-row);
  padding: var(--space-2) var(--space-18px);
  border-top: var(--border-hairline) solid var(--border);
}

.groupRow:first-child {
  border-top: 0;
}

/* Profil's `.tight` cut: one step less between its groups, which is what lets the tab's three groups
 * stand whole on a 1280x720 laptop. */
.tight .group + .groupLabel,
.tight .group + .group {
  margin-top: var(--space-4);
}

/* The row's name, in its own column so every control on a card starts on one line. */
.rowLabel {
  flex: none;
  width: var(--w-account-row-label);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-semibold);
  color: var(--text);
}

.rowLabelDanger {
  color: var(--danger-text-strong);
}

.rowBody {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-note);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

.rowActions {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  margin-left: auto;
}

/* A footnote under a group: one quiet line. */
.groupHint {
  margin-top: var(--space-2);
  font-size: var(--fs-micro);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* ── The right column ────────────────────────────────────────────────────────────────────── */

/* Centred on its own axis, so a short card sits on the screen's middle line rather than at its top
 * — the mockup's arrangement, and what makes the column read as a companion to the sheet rather
 * than as a second column of content. */
.side {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: var(--space-7) var(--space-8);
  overflow: hidden auto;
  background: var(--bg-nav);
}

.sidePanel {
  width: 100%;
  max-width: var(--w-account-side-card);
}

/* A card in that column: the mockup's `.scard`, at its own 22px, and an ORDINARY card — the column
 * under it is `--bg-nav`, the rail's own near-black, so a card standing on it is the plain
 * `--bg-card` this app draws everywhere else rather than a card-in-card.
 *
 * `--space-22px`, not `--space-5-5px`: the `-px` tokens are their own NUMBER, so the scale's 5.5th
 * step is 5.5 pixels. The card was drawing a 5.5px inset, which is the „cramped at the top" the
 * owner reported. */
.card {
  padding: var(--space-22px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-20px);
  box-shadow: var(--shadow-card);
}

.cardTitle {
  margin-bottom: var(--space-1);
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.cardBody {
  font-size: var(--fs-label);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* Below this the sheet has the frame to itself. Hidden rather than narrowed — see the header. */
@media (width < 1080px) {
  .columns {
    grid-template-columns: minmax(0, 1fr);
  }

  .side {
    display: none;
  }

  .sheet {
    border-right: 0;
  }
}
